/**
 * Signature flags service for counter pickup flow.
 * Fetches which forms require a signature and submits captured signature to append to PDFs.
 */

/**
 * Build prescriptions array for the signature-requirements API from counter orders.
 * Each order's items (with PrescriptionFlags or Flags) become one prescription per item.
 * @param {Array<{ id: string|number }>} orders - Unprocessed orders
 * @param {Record<string|number, { items?: Array<{ id?: string|number, prescription_name?: string, PrescriptionFlags?: Array<{ label?: string, code?: string, id?: string }>, Flags?: Array<{ label?: string, code?: string, id?: string }> }> }>} orderFullDetailsMap
 * @returns {Array<{ id: string, flags: string[] }>}
 */
export function buildPrescriptionsFromOrders(orders, orderFullDetailsMap) {
  const prescriptions = [];
  if (!Array.isArray(orders)) return prescriptions;

  for (const order of orders) {
    if (!order || order.id == null) continue;
    const details = orderFullDetailsMap[order.id] || order;
    const items = details.items != null ? details.items : (order.items != null ? order.items : []);
    const itemList = Array.isArray(items) ? items : [items];

    for (const item of itemList) {
      if (!item) continue;
      const id = item.id != null ? item.id : (item.prescription_name != null ? item.prescription_name : (item.itemId != null ? item.itemId : order.id));
      const rawFlags = item.PrescriptionFlags != null ? item.PrescriptionFlags : (item.Flags != null ? item.Flags : (item.flags != null ? item.flags : []));
      const flagList = Array.isArray(rawFlags) ? rawFlags : [];
      const flags = flagList
        .map((f) => (typeof f === "string" ? f : f && (f.label || f.code || f.id) ? String(f.label || f.code || f.id) : null))
        .filter(Boolean);
      prescriptions.push({ id: String(id), flags });
    }
  }
  return prescriptions;
}

function getAuthToken() {
  try {
    const user = JSON.parse(sessionStorage.getItem("user"));
    return (
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken
    );
  } catch (e) {
    return null;
  }
}

function getBaseUrl() {
  const base = process.env.REACT_APP_BASEURL || "";
  return base.replace(/\/+$/, "") + (base ? "/" : "");
}

/**
 * Fetch account signature flag definitions (T&C Prescription Signature Flags). Used when accountDetails
 * has no signature_flags_test_config so we still send flags in signature-requirements request.
 * @param {string|number} accountId
 * @returns {Promise<string[]>} Array of flag codes (externalCodes or label per def) to send in prescriptions.flags
 */
export async function fetchAccountSignatureFlagCodes(accountId) {
  const base = getBaseUrl();
  const url = base + "accounts/" + encodeURIComponent(String(accountId)) + "/signature-flags";
  const token = getAuthToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = "Bearer " + token;
  const response = await fetch(url, { method: "GET", withCredentials: true, headers });
  if (!response.ok) return [];
  const defs = await response.json();
  if (!Array.isArray(defs)) return [];
  const seen = {};
  return defs
    .flatMap(function (d) {
      const codes = d.externalCodes || d.external_codes;
      if (Array.isArray(codes) && codes.length > 0) return codes.map(function (c) { return String(c).trim(); }).filter(Boolean);
      const label = d.label != null ? String(d.label).trim() : "";
      return label ? [label] : [];
    })
    .filter(function (code) {
      if (seen[code]) return false;
      seen[code] = true;
      return true;
    });
}

/**
 * Fetch which signature forms are required for this pickup (counter context).
 * @param {string|number} pickupId
 * @param {string|number} accountId
 * @param {Array<{ id: string|number, flags: string[] }>} prescriptions
 * @returns {Promise<{ pickupId: string, requirements: Array<{ flagLabel: string, displayName?: string, formUrl?: string, formVersion?: number, prescriptionIds: string[], missingForm: boolean }> }>}
 */
export async function fetchSignatureRequirements(pickupId, accountId, prescriptions) {
  const token = getAuthToken();
  const base = getBaseUrl();
  const url = base + "pickup/" + encodeURIComponent(String(pickupId)) + "/signature-requirements";
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = "Bearer " + token;
  }
  const response = await fetch(url, {
    method: "POST",
    withCredentials: true,
    headers: headers,
    body: JSON.stringify({
      accountId: String(accountId),
      context: "counter",
      prescriptions: (prescriptions || []).map((p) => ({
        id: String(p.id),
        flags: Array.isArray(p.flags) ? p.flags : [],
      })),
    }),
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(`Failed to fetch signature requirements: ${response.statusText}${errText ? ` ${errText}` : ""}`);
  }
  return response.json();
}

/**
 * Submit a captured signature for one required flag. API merges signature onto form and stores signed PDF.
 * @param {string|number} pickupId
 * @param {string|number} accountId
 * @param {string} flagLabel
 * @param {string} signatureImage - base64 data URL
 * @param {{ signerName?: string, formVersion?: number, metadata?: object }} options
 */
export async function submitSignatureForFlag(pickupId, accountId, flagLabel, signatureImage, options = {}) {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const base = getBaseUrl();
  const encodedLabel = encodeURIComponent(flagLabel);
  const url = `${base}pickup/${encodeURIComponent(String(pickupId))}/signature-flags/${encodedLabel}/sign`;
  const response = await fetch(url, {
    method: "POST",
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      accountId: String(accountId),
      formVersion: options.formVersion != null ? options.formVersion : undefined,
      signatureImage,
      signerName: options.signerName != null ? options.signerName : undefined,
      signedAt: new Date().toISOString(),
      deviceId: options.deviceId != null ? String(options.deviceId) : undefined,
      metadata: options.metadata != null ? options.metadata : undefined,
    }),
  });
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(`Failed to submit signature for flag: ${response.statusText}${errText ? ` ${errText}` : ""}`);
  }
  return response.json();
}

/**
 * Submit the same captured signature for all required flags (skips those with missingForm).
 * @param {string|number} pickupId
 * @param {string|number} accountId
 * @param {string} signatureImage - base64 data URL
 * @param {Array<{ flagLabel: string, displayName?: string, formUrl?: string, formVersion?: number, prescriptionIds?: string[], missingForm?: boolean }>} requirements
 * @param {{ signerName?: string, metadata?: object }} options
 */
export async function submitSignatureFlagsForRequirements(
  pickupId,
  accountId,
  signatureImage,
  requirements,
  options = {}
) {
  const toSubmit = (requirements || []).filter((r) => !r.missingForm);
  for (const req of toSubmit) {
    await submitSignatureForFlag(pickupId, accountId, req.flagLabel, signatureImage, {
      formVersion: req.formVersion != null ? req.formVersion : undefined,
      signerName: options.signerName,
      metadata: { ...(options.metadata || {}), prescriptionIds: req.prescriptionIds },
    });
  }
}

export { getAuthToken };



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/services/SignatureFlagsService.js